import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Arrow, Swipe, cx, prefersReducedMotion, usePageMeta } from '../ui';
import { BookingWidget } from '../booking/BookingWidget';
import { CHAPTERS, END, LAST, PERSONA, PHOTO, STEPS, UNIT, type Persona, type Step, type Surface } from './data';
import { Icon, Photo, PhoneFrame, RatingPlate, UnitPhoto } from './parts';
import { PhoneScreen } from './PhoneScreens';
import { AdminScreen } from './AdminScreens';
import { TOUR_CSS } from './styles';

// ---------------------------------------------------------------- helpers
const parseStep = (v: string | null) => {
  if (!v) return 0;
  if (v === 'end') return END;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 1 && n <= LAST ? n : 0;
};

const tourUrl = () => (typeof window === 'undefined' ? 'https://www.brandedmark.com/tour' : `${window.location.origin}/tour`);

function useScale() {
  const [s, setS] = useState(1);
  useEffect(() => {
    const f = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const avail = w >= 1024 ? h - 72 - 168 : h - 72 - 92 - 236;
      setS(Math.max(w >= 1024 ? 0.8 : 0.64, Math.min(1, avail / 600)));
    };
    f();
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  return s;
}

// ---------------------------------------------------------------- page
export default function Tour() {
  usePageMeta(
    'Interactive tour: one heat pump, end to end',
    'Click through one heat pump’s life: commissioning, warranty, the homeowner’s scan, a grounded answer, a fault, a bulletin, the house sale, its passport and its recycling. About three minutes.'
  );
  const [params, setParams] = useSearchParams();
  const step = parseStep(params.get('step'));
  const sectionRef = useRef<HTMLElement>(null);

  const go = useCallback(
    (n: number) => {
      const p = new URLSearchParams(params);
      if (n <= 0) p.delete('step');
      else p.set('step', n >= END ? 'end' : String(n));
      setParams(p, { replace: step !== 0 && n !== 0 });
    },
    [params, setParams, step]
  );

  // keep the tour at the top of the viewport when moving between steps
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    if (window.scrollY > top + 4) window.scrollTo({ top, behavior: 'auto' });
  }, [step]);

  return (
    <section ref={sectionRef} className="bmt" aria-label="Interactive product tour">
      <style>{TOUR_CSS}</style>
      {step === 0 && <Intro go={go} />}
      {step >= 1 && step <= LAST && <Stage step={step} go={go} />}
      {step === END && <End go={go} />}
    </section>
  );
}

// ---------------------------------------------------------------- intro
function Intro({ go }: { go: (n: number) => void }) {
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [go]);

  return (
    <div className="wrap grid items-center gap-12 pb-20 pt-10 lg:min-h-[calc(100svh-72px)] lg:grid-cols-[1fr_1.05fr] lg:gap-16 lg:py-14">
      <div>
        <h1 className="t-h1 max-w-[12ch]">
          See it on one <Swipe draw>heat pump.</Swipe>
        </h1>
        <p className="t-lede mt-6 max-w-[34rem]">
          Follow one heat pump from the installer’s first scan to its recycling.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-4">
          <button type="button" onClick={() => go(1)} className="btn btn-primary">
            Start the tour <Arrow />
          </button>
          <button type="button" onClick={() => go(8)} className="text-[15px] font-semibold text-bm-muted underline decoration-bm-line decoration-2 underline-offset-[5px] hover:text-bm-indigo hover:decoration-bm-indigo">
            or skip to the brand view
          </button>
        </div>
        <p className="mt-6 text-[14px] text-bm-muted">{LAST} steps, about 3 minutes. No sign-up. Arrow keys work too.</p>
      </div>

      <IntroVisual onStart={() => go(1)} />
    </div>
  );
}

function IntroVisual({ onStart }: { onStart: () => void }) {
  return (
    <figure className="relative">
      <div className="frame">
        <div>
        <div className="relative overflow-hidden bg-[#181343]">
          <Photo
            src={PHOTO.scene}
            fallback={PHOTO.sceneFallback}
            alt={UNIT.sceneAlt}
            width={1600}
            height={1067}
            eager
            className="block aspect-[4/3] w-full sm:aspect-[3/2]"
          />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24" style={{ background: 'linear-gradient(180deg, rgba(24,19,67,.55), rgba(24,19,67,0))' }} />
          <div className="absolute left-4 top-4 text-[12px] text-white sm:left-6 sm:top-5">
            <div className="font-medium text-white/75">The unit you’ll follow</div>
            <div className="mt-0.5 font-semibold">Halvard AW-9 air source heat pump</div>
          </div>
          {/* the rating plate, with Halvard's code designed into it */}
          <div className="absolute bottom-3 right-3 w-[188px] sm:bottom-6 sm:right-6 sm:w-[252px]">
            <button type="button" onClick={onStart} className="group block w-full text-left" aria-label="Start the tour at the rating plate">
              <span className="mb-1.5 hidden text-[12px] font-semibold sm:block text-white/90 [text-shadow:0_1px_6px_rgba(0,0,0,.5)]">The rating plate on the unit</span>
              <RatingPlate qr={64} responsive />
              <span className="mt-2 flex items-center justify-between rounded-full bg-[#241E61] px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors group-hover:bg-[#181343]">
                Scan to begin <Arrow />
              </span>
            </button>
          </div>
        </div>
        <dl className="grid grid-cols-3 border-t border-bm-line bg-white text-[13px]">
          {[
            ['Made by', 'Halvard'],
            ['Installed by', 'Brightwell Heating'],
            ['Home', '14 Orchard Lane'],
          ].map(([k, v], i) => (
            <div key={k} className={cx('min-w-0 px-3 py-3 sm:px-6 sm:py-3.5', i > 0 && 'border-l border-bm-line')}>
              <dt className="text-[11.5px] text-bm-muted">{k}</dt>
              <dd className="font-semibold leading-snug text-bm-indigo">{v}</dd>
            </div>
          ))}
        </dl>
        </div>
      </div>
      <figcaption className="mt-3 text-[13px] text-bm-muted">Brands, people and prices in this tour are illustrative. The flows are how the product works, simplified for the tour.</figcaption>
    </figure>
  );
}

// ---------------------------------------------------------------- stage
function Stage({ step, go }: { step: number; go: (n: number) => void }) {
  const s = STEPS[step - 1];
  const scale = useScale();
  const [acting, setActing] = useState(false);
  const [view, setView] = useState<Surface>(s.focus);
  const [bannerFor, setBannerFor] = useState(0);
  const lastPersona = useRef<Persona | null>(null);
  const timer = useRef<number>();
  const stageRef = useRef<HTMLDivElement>(null);

  // reset per step
  useLayoutEffect(() => {
    window.clearTimeout(timer.current);
    setActing(false);
    setView(s.focus);
    if (lastPersona.current !== s.persona) setBannerFor(step);
    lastPersona.current = s.persona;
  }, [step, s.focus, s.persona]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // bring the hotspot into view inside a scrolled surface (mobile)
  useEffect(() => {
    const hit = stageRef.current?.querySelector<HTMLElement>(`.bmt-${view} .bmt-hit`);
    if (hit && window.innerWidth < 1024) hit.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [view, step]);

  const act = useCallback(() => {
    if (acting) return;
    if (s.hold) {
      setActing(true);
      if (s.actView) setView(s.actView);
      return;
    }
    setActing(true);
    if (s.actView) setView(s.actView);
    const ms = prefersReducedMotion() ? Math.min(400, s.actMs ?? 350) : s.actMs ?? 350;
    timer.current = window.setTimeout(() => go(step + 1), ms);
  }, [acting, s, step, go]);

  const next = useCallback(() => {
    if (s.hold || acting) {
      window.clearTimeout(timer.current);
      go(step + 1);
    } else act();
  }, [s.hold, acting, act, go, step]);

  const back = useCallback(() => go(step - 1), [go, step]);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      } else if (e.key === 'Escape') go(0);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [next, back, go]);

  const hotPhone = s.focus === 'phone' && !acting;
  const hotAdmin = s.focus === 'admin' && !acting;
  const screenProps = { step, acting, onAct: act, hotLabel: s.hot };
  const phoneH = 600 * scale;
  const persona: Persona = acting && step === 9 ? 'newowner' : s.persona;

  return (
    <div className="pb-[236px] lg:pb-12">
      <h1 className="sr-only">BrandedMark interactive tour</h1>
      <TopBar step={step} go={go} view={view} setView={setView} s={s} />

      <div className="bmt-wide grid lg:grid-cols-[minmax(300px,340px)_1fr] lg:gap-10 xl:gap-14">
        {/* the step card: left rail on desktop, bottom sheet on mobile */}
        <aside className="lg:pt-1">
          <div className="lg:sticky lg:top-[96px]">
            <StepCard s={s} step={step} acting={acting} next={next} back={back} />
          </div>
        </aside>

        <div ref={stageRef} className="bmt-stage min-w-0" data-view={view}>
          <UnitBar s={s} acting={acting} step={step} />
          <div className="mt-4 flex items-start justify-center gap-6 lg:mt-5 lg:justify-start xl:gap-8">
            <div className={cx('bmt-surface bmt-phone', s.focus !== 'phone' && !(acting && s.actView === 'phone') && 'is-dim')}>
              <div className="bmt-cap hidden lg:flex">
                <span className="bmt-live" />
                <span>
                  Phone · <b>{PERSONA[persona].who}</b>, {PERSONA[persona].role.toLowerCase()}
                </span>
              </div>
              <PhoneFrame scale={scale}>
                <PhoneScreen {...screenProps} hot={hotPhone} persona={s.persona} personaChanged={bannerFor === step} />
              </PhoneFrame>
            </div>
            <div className={cx('bmt-surface bmt-admin w-full min-w-0 max-w-[440px] lg:max-w-[640px] lg:flex-1', s.focus !== 'admin' && 'is-dim')}>
              <div className="bmt-cap hidden lg:flex">
                <span className="bmt-live" />
                <span>
                  <b>Halvard</b>’s view in BrandedMark
                </span>
              </div>
              <div className="overflow-hidden rounded-2xl bg-white" style={{ height: phoneH, boxShadow: '0 30px 70px -34px rgba(24,19,67,.4), 0 0 0 1px #E4E3F0' }}>
                <div className="flex items-center gap-2 border-b border-[#E4E3F0] bg-[#F7F7FC] px-4 py-2.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#E4E3F0]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#E4E3F0]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#E4E3F0]" />
                  <span className="ml-3 truncate font-mono text-[11px] text-[#67648F]">admin.brandedmark.com</span>
                </div>
                <div className="h-[calc(100%-37px)] overflow-y-auto overflow-x-hidden lg:overflow-hidden">
                  <AdminScreen {...screenProps} hot={hotAdmin} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TopBar({ step, go, view, setView, s }: { step: number; go: (n: number) => void; view: Surface; setView: (v: Surface) => void; s: Step }) {
  return (
    <div className="bmt-wide pb-2 pt-3 lg:pb-4 lg:pt-6">
      <div className="flex items-center gap-4 lg:gap-6">
        <div className="hidden shrink-0 text-[13px] font-semibold text-bm-indigo lg:block">One heat pump, end to end</div>
        <nav className="flex min-w-0 flex-1 gap-2 lg:gap-3" aria-label="Tour progress">
          {CHAPTERS.map((c) => (
            <div key={c.name} className="min-w-0" style={{ flex: Math.max(c.steps.length, c.name.length / 11) }}>
              <div className={cx('hidden truncate text-[11.5px] font-medium lg:block', c.steps.includes(step) ? 'text-bm-indigo' : 'text-bm-muted')}>{c.name}</div>
              <div className="flex gap-1">
                {c.steps.map((n) => (
                  <button key={n} type="button" className="bmt-seghit" onClick={() => go(n)} aria-label={`Step ${n}: ${STEPS[n - 1].title}`} aria-current={n === step ? 'step' : undefined}>
                    <span className={cx('bmt-seg', n < step && 'is-done', n === step && 'is-now')} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <button type="button" onClick={() => go(0)} className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold text-bm-indigo shadow-[inset_0_0_0_1.5px_#E4E3F0] hover:shadow-[inset_0_0_0_1.5px_#241E61]" aria-label="Exit the tour">
          <Icon.x size={14} />
          <span className="hidden sm:inline">Exit</span>
          <span className="hidden font-mono text-[11px] font-normal text-bm-muted lg:inline">Esc</span>
        </button>
      </div>
      {/* mobile: which surface */}
      <div className="mt-2 flex items-center justify-between gap-3 lg:hidden">
        <div className="min-w-0 truncate text-[12.5px] text-bm-muted">
          <b className="font-semibold text-bm-indigo">{step}/{LAST}</b> · {s.when}
        </div>
        <div className="flex shrink-0 rounded-full bg-white p-1 shadow-[inset_0_0_0_1px_#E4E3F0]" role="tablist" aria-label="Choose a view">
          {(['phone', 'admin'] as Surface[]).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              onClick={() => setView(v)}
              className={cx('relative inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold', view === v ? 'bg-bm-indigo text-white' : 'text-bm-indigo')}
            >
              {v === 'phone' ? <Icon.phone size={13} /> : <Icon.window size={13} />}
              {v === 'phone' ? 'Phone' : 'Brand view'}
              {s.focus === v && view !== v && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[#3FAE6C]" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function UnitBar({ s, acting, step }: { s: Step; acting: boolean; step: number }) {
  const stage =
    step === 9 && acting ? 'New owners' :
    step === 7 && acting ? 'Case open' :
    step === 2 && acting ? 'Commissioned' :
    step === 11 && acting ? 'Take-back booked' :
    s.stage === 'Sold with the house' ? 'In use' : s.stage;
  const holder = step === 9 && acting ? 'The new owners' : step === 2 && acting ? '14 Orchard Lane' : s.holder;
  const facts: [string, string][] = [
    ['Stage', stage],
    ['With', holder],
    ['When', s.when],
  ];
  return (
    <div className="hidden items-center gap-5 rounded-[18px] bg-white px-3 py-2.5 shadow-[0_0_0_1px_#E4E3F0] lg:flex">
      <div className="flex min-w-0 items-center gap-3">
        <UnitPhoto className="h-12 w-16 shrink-0 rounded-[12px]" />
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[14px] font-bold text-bm-indigo">Halvard AW-9 air source heat pump</div>
          <div className="font-mono text-[12px] text-bm-muted">{UNIT.serial}</div>
        </div>
      </div>
      <dl className="ml-auto flex shrink-0 divide-x divide-bm-line text-[12.5px]">
        {facts.map(([k, v]) => (
          <div key={k} className="px-4 leading-tight first:pl-0 last:pr-0">
            <dt className="text-[11px] text-bm-muted">{k}</dt>
            <dd key={v} className="bmt-flash -mx-1 mt-0.5 whitespace-nowrap px-1 font-semibold text-bm-indigo">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function StepCard({ s, step, next, back }: { s: Step; step: number; acting: boolean; next: () => void; back: () => void }) {
  const isLast = step === LAST;
  return (
    <div className="bmt-card on-dark p-4 sm:p-5 lg:p-7">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[13.5px] font-semibold text-[#CFF710]">
          Step {step} of {LAST}
        </div>
      </div>
      <h2 key={step} className="bmt-screen mt-2 text-[19px] font-extrabold leading-[1.15] tracking-[-0.02em] text-white lg:mt-4 lg:text-[27px] lg:leading-[1.1]" aria-live="polite">
        {s.title}
      </h2>
      <p className="bmt-why mt-1.5 text-[14px] leading-[1.5] text-white/75 lg:mt-3 lg:text-[16px] lg:leading-[1.55]">{s.why}</p>
      <div className="mt-3 flex items-center gap-2 lg:mt-6">
        <button type="button" onClick={back} className="btn btn-ghost btn-sm !px-3.5" aria-label="Previous step">
          <Icon.arrowL size={16} />
          <span className="hidden sm:inline">Back</span>
        </button>
        <button type="button" onClick={next} className="btn btn-go btn-sm flex-1 lg:flex-none">
          {isLast ? 'Finish' : 'Next'}
          <Arrow />
        </button>
        <span className="ml-auto hidden items-center gap-1 text-[11.5px] text-white/50 xl:flex">
          <kbd className="bmt-key">←</kbd>
          <kbd className="bmt-key">→</kbd>
        </span>
      </div>
      <p className="mt-4 hidden text-[12.5px] leading-snug text-white/55 lg:block">Click the pulsing ring, or press Next. The other screen updates as you go.</p>
    </div>
  );
}

// ---------------------------------------------------------------- end
function End({ go }: { go: (n: number) => void }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.key === 'ArrowLeft') go(LAST);
      if (e.key === 'Escape') go(0);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [go]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tourUrl());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      window.prompt('Copy this link', tourUrl());
    }
  };
  const mail = `mailto:?subject=${encodeURIComponent('A 3-minute tour: one heat pump, end to end')}&body=${encodeURIComponent(
    `This follows one heat pump from the installer's first scan to the next owners and its recycling, with the maker's view alongside. Worth three minutes:\n\n${tourUrl()}`
  )}`;

  return (
    <div className="wrap grid items-start gap-12 pb-20 pt-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16 lg:py-16">
      <div>
        <h1 className="t-h2 max-w-[15ch]">
          A record like this for your <Swipe draw>whole range.</Swipe>
        </h1>
        <p className="t-lede mt-6 max-w-[36rem]">In your brand, and connected to your ERP.</p>

        <p className="mt-8 text-[14.5px] text-bm-muted">
          Know someone who should see this?{' '}
          <button type="button" onClick={copy} className="font-semibold text-bm-indigo underline decoration-bm-line decoration-2 underline-offset-[5px] hover:decoration-bm-indigo" aria-live="polite">
            {copied ? 'Link copied' : 'Copy the link to this tour'}
          </button>
          {' '}or{' '}
          <a href={mail} className="font-semibold text-bm-indigo underline decoration-bm-line decoration-2 underline-offset-[5px] hover:decoration-bm-indigo">
            email it
          </a>
          .
        </p>
        <p className="mt-10 text-[12.5px] text-bm-muted/80">Brands, people and prices in this tour are illustrative.</p>
      </div>

      <div>
        <div className="min-w-0 lg:sticky lg:top-[96px]">
          <BookingWidget compact source="tour" />
        </div>
      </div>
    </div>
  );
}

